import React, { useState } from 'react';
import Header from '../components/header';
import DefaultFooter from '../components/default-footer';

// ============================================================
// Services.tsx
//
// Premium Services page - chaque carte est un lien cliquable
// vers une page dédiée à chaque service.
// ============================================================

interface Service {
  id: string;
  title: string;
  description: string;
  image: string;
  category: string;
  size: 'lg' | 'md';
  slug: string; 
}

interface Strength {
  title: string;
  description: string;
}

// ============================================================
// SERVICES AVEC LEURS SLUGS (URLS)
// ============================================================

const services: Service[] = [
  { 
    id: 'corporate-travel', 
    title: 'Corporate Travel Management', 
    description: 'Managing your business trips seamlessly, from booking to reporting.', 
    image: 'https://images.unsplash.com/photo-1431540015161-0bf868a2d407?w=1200&q=80&fit=crop&crop=center', 
    category: 'Business', 
    size: 'lg',
    slug: '/services/corporate-travel' 
  },
  { 
    id: 'airline-reservations', 
    title: 'Airline Reservations', 
    description: 'Expertly managed flight reservations for stress-free travel.', 
    image: 'https://images.unsplash.com/photo-1688751582902-914dbae7a69b?w=900&q=80&fit=crop&crop=center', 
    category: 'Flights', 
    size: 'md',
    slug: '/services/airline-reservations' 
  },
  { 
    id: 'meet-greet', 
    title: 'Meet & Greet', 
    description: 'Personalized assistance from arrival to departure.', 
    image: 'https://images.unsplash.com/photo-1762818106013-74323d5fcc80?w=900&q=80&fit=crop&crop=center', 
    category: 'Airport Services', 
    size: 'md',
    slug: '/services/meet-greet' 
  },
  { 
    id: 'uae-visa', 
    title: 'GCC Visa', 
    description: 'Fast & hassle-free UAE visa processing.', 
    image: 'https://images.unsplash.com/photo-1593781492912-bb538a002c02?w=900&q=80&fit=crop&crop=center', 
    category: 'Visa', 
    size: 'md',
    slug: '/services/uae-visa' 
  },
  { 
    id: 'inbound-holidays', 
    title: 'Inbound Holidays', 
    description: 'Plan and organise leisure travel, business travel, group travel or MICE.', 
    image: 'https://images.unsplash.com/photo-1699855803432-17f4ab468b2d?w=900&q=80&fit=crop&crop=center', 
    category: 'Holidays', 
    size: 'md',
    slug: '/services/inbound-holidays' 
  },
  { 
    id: 'global-visa', 
    title: 'Global Visa', 
    description: 'Worldwide visa assistance made simple.', 
    image: 'https://images.unsplash.com/photo-1593781492912-bb538a002c02?w=900&q=80&fit=crop&crop=center', 
    category: 'Visa', 
    size: 'md',
    slug: '/services/global-visa' 
  },
  { 
    id: 'rail-europe', 
    title: 'Rail Europe', 
    description: 'Explore Europe effortlessly with seamless train bookings.', 
    image: 'https://images.unsplash.com/photo-1486915585284-e6cb5cd1ec87?w=900&q=80&fit=crop&crop=center', 
    category: 'Rail', 
    size: 'md',
    slug: '/services/rail-europe' 
  },
  { 
    id: 'cruise-holidays', 
    title: 'Cruise Holidays', 
    description: 'Experience the magic of the sea on a cruise trip.', 
    image: 'https://images.unsplash.com/photo-1572018093802-1e39c535d31a?w=1200&q=80&fit=crop&crop=center', 
    category: 'Cruise', 
    size: 'lg',
    slug: '/services/cruise-holidays' 
  },
  { 
    id: 'worldwide-hotels', 
    title: 'Worldwide Hotels', 
    description: 'Find the perfect stay anytime, anywhere.', 
    image: 'https://images.unsplash.com/photo-1567636788276-40a47795ba4d?w=900&q=80&fit=crop&crop=center', 
    category: 'Stays', 
    size: 'md',
    slug: '/services/worldwide-hotels' 
  },
  { 
    id: 'mice', 
    title: 'MICE', 
    description: 'Tailored meetings, incentives, conferences & events worldwide.', 
    image: 'https://images.unsplash.com/photo-1431540015161-0bf868a2d407?w=900&q=80&fit=crop&crop=center', 
    category: 'Business', 
    size: 'md',
    slug: '/services/mice' 
  },
  { 
    id: 'car-rental', 
    title: 'Car Rental', 
    description: 'Flexible, reliable mobility wherever you go.', 
    image: 'https://images.unsplash.com/photo-1760976396211-5546ce83a400?w=900&q=80&fit=crop&crop=center', 
    category: 'Ground', 
    size: 'md',
    slug: '/services/car-rental' 
  },
  { 
    id: 'religious-tourism', 
    title: 'Religious Tourism', 
    description: 'Travel with purpose, comfort, and complete peace of mind.', 
    image: 'https://images.unsplash.com/photo-1693590614566-1d3ea9ef32f7?w=900&q=80&fit=crop&crop=center', 
    category: 'Pilgrimage', 
    size: 'md',
    slug: '/services/religious-tourism' 
  },
];

// ============================================================
// CATÉGORIES (badges de filtre) — dérivées de la liste ci-dessus,
// avec une icône dédiée par catégorie pour un rendu plus soigné.
// ============================================================

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  Business: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  Flights: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-1 .1-1.3.5l-.6.7c-.5.6-.4 1.4.3 1.8L9 12l-2 3H4l-1 1 3 1 1 3 1-1v-3l3-2 2.5 5.3c.4.7 1.2.8 1.8.3l.7-.6c.4-.3.6-.8.5-1.3z" />
    </svg>
  ),
  'Airport Services': (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="7" width="12" height="14" rx="2" /><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /><path d="M9 12h.01M15 12h.01M9 16h.01M15 16h.01" />
    </svg>
  ),
  Visa: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="M15 8h3M15 12h3M6 17c.5-2 2-3 3-3s2.5 1 3 3" />
    </svg>
  ),
  Holidays: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  ),
  Rail: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="5" y="3" width="14" height="14" rx="4" /><path d="M9 17l-2 4M15 17l2 4M5 10h14" /><circle cx="9" cy="13" r="1" /><circle cx="15" cy="13" r="1" />
    </svg>
  ),
  Cruise: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 20.5c1.5 1.2 3 1.2 4.5 0s3-1.2 4.5 0 3 1.2 4.5 0 3-1.2 4.5 0" />
      <path d="M4 15l1-8h4l1 3M12 10V4h3l2 6" /><path d="M4 15h16l-2 5H6z" />
    </svg>
  ),
  Stays: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 18v-7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v7" /><path d="M3 18h18M5 11V6a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v5" /><path d="M3 21v-3M21 21v-3" />
    </svg>
  ),

  Ground: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 17h14M5 17a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM19 17a2 2 0 1 0 4 0 2 2 0 0 0-4 0zM3 17V9l2-4h10l4 4h2v8" />
    </svg>
  ),
  Pilgrimage: (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18M5 21V10l7-6 7 6v11M9 21v-6h6v6" />
    </svg>
  ),
};

const DEFAULT_CATEGORY_ICON = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
  </svg>
);

const CATEGORIES = Array.from(new Set(services.map((s) => s.category)));

// ============================================================
// COMPOSANTS
// ============================================================

function DuneRule() {
  return (
    <div aria-hidden="true" style={{ lineHeight: 0 }}>
      <svg viewBox="0 0 1200 34" width="100%" height="22" preserveAspectRatio="none">
        <path d="M0 24 C 90 4, 170 4, 250 20 S 400 30, 480 14 S 620 2, 700 18 S 850 30, 940 12 S 1090 2, 1200 16" fill="none" stroke="var(--gold-d)" strokeOpacity="0.55" strokeWidth="1.5" />
        <path d="M0 28 C 90 12, 170 12, 250 26 S 400 34, 480 20 S 620 10, 700 24 S 850 34, 940 18 S 1090 10, 1200 22" fill="none" stroke="rgba(41,75,90,.14)" strokeWidth="1" />
      </svg>
    </div>
  );
}

function ServiceCard({ service }: { service: Service }) {
  return (
    <a 
      href={service.slug}  // ✅ LIEN VERS LA PAGE DÉDIÉE
      className={`svc-card ${service.size === 'lg' ? 'svc-card-lg' : ''}`}
    >
      <div className="svc-card-media" style={{ backgroundImage: `url(${service.image})` }} />
      <div className="svc-card-veil" />
      <span className="svc-chip" aria-hidden="true">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M7 17 17 7M9 7h8v8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <div className="svc-card-content">
        <span className="svc-card-tag">{service.category}</span>
        <div className="svc-card-title">{service.title}</div>
        <p className="svc-card-desc">{service.description}</p>
      </div>
    </a>
  );
}

const featured = {
  category: 'Signature Experience',
  title: 'Outbound Holidays',
  description:
    "From the boulevards of Paris to the skylines of the Far East — we design outbound journeys with the same care we bring to every visa and reservation: precise, personal, and unhurried.",
  image: 'https://images.unsplash.com/photo-1486915585284-e6cb5cd1ec87?w=1200&q=80&fit=crop&crop=center',
  cta: 'Discover Outbound Holidays',
  slug: '/services/outbound-holidays', // ✅ NOUVEAU
};



const strengths: Strength[] = [
  { title: 'Local Expertise', description: 'Deep, on-the-ground knowledge of regional travel, documentation, and connections.' },
  { title: 'Personalized Experiences', description: 'Every itinerary is shaped around how you actually want to travel — not a fixed package.' },
  { title: 'Professional Service', description: 'A dedicated team handling documents, bookings, and logistics with precision.' },
  { title: 'Reliable Support', description: "From first enquiry to landing home, we're reachable when it matters." },
];

// ============================================================
// PAGE PRINCIPALE
// ============================================================

export default function Services() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const filteredServices = activeCategory
    ? services.filter((s) => s.category === activeCategory)
    : services;

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
          --radius: 10px;
          --radius-sm: 5px;
          --shadow-sm: 0 6px 20px rgba(26, 49, 61, .07);
          --shadow-md: 0 20px 48px rgba(26, 49, 61, .12);
          --shadow-lg: 0 28px 64px rgba(26, 49, 61, .18);
        }
        * { box-sizing: border-box; }
        .visa-page { font-family: 'DM Sans', sans-serif; background: var(--cream); -webkit-font-smoothing: antialiased; }
        .visa-page h1, .visa-page h2, .visa-page h3, .visa-page h4 { font-family: 'Playfair Display', serif; }
        .visa-page a:focus-visible, .visa-page button:focus-visible { outline: 2px solid var(--gold-d); outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce) { .visa-page *, .visa-page *::before, .visa-page *::after { animation-duration: .01ms !important; transition-duration: .01ms !important; } }

        .eyebrow { display: inline-flex; align-items: center; gap: 10px; font-size: 11px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: var(--gold-d); }
        .eyebrow::before { content: ''; width: 22px; height: 2px; background: var(--gold); border-radius: 2px; }
        .eyebrow.light { color: var(--gold-l); }
        .eyebrow.light::before { background: var(--gold-l); opacity: .6; }

        .section-title { font-size: clamp(1.7rem, 2.6vw, 2.4rem); font-weight: 500; color: var(--ink); line-height: 1.2; letter-spacing: -.01em; margin: 10px 0 12px; }
        .section-sub { font-size: 15px; color: var(--silver); font-weight: 300; line-height: 1.7; max-width: 520px; }
        .section-pad { padding: 84px 0; }
        @media (max-width: 991px) { .section-pad { padding: 64px 0; } }
        @media (max-width: 576px) { .section-pad { padding: 48px 0; } }
        .bg-cream { background: var(--cream); }
        .bg-white { background: #fff; }

        /* HERO */
        .visa-hero { position: relative; min-height: 460px; display: flex; align-items: center; overflow: hidden; background: var(--teal-d); }
        .visa-hero-img { position: absolute; inset: 0; background-size: cover; background-position: center; opacity: .38; }
        .visa-hero-veil { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(26,49,61,.55) 0%, rgba(26,49,61,.92) 100%); }
        .visa-hero-inner { position: relative; z-index: 2; padding: 64px 0 56px; }
        .visa-hero-title { font-weight: 600; color: #fff; line-height: 1.16; font-size: clamp(30px, 4vw, 48px); max-width: 640px; margin-bottom: 16px; }
        .visa-hero-title span.gold { font-style: italic; color: var(--gold); font-weight: 400; }
        .visa-hero-sub { font-size: 15px; color: rgba(255,255,255,.78); font-weight: 300; line-height: 1.75; max-width: 540px; margin-bottom: 30px; }
        .visa-hero-ctas { display: flex; gap: 12px; flex-wrap: wrap; }

        .vbtn-primary { display: inline-flex; align-items: center; gap: 8px; background: var(--gold); color: var(--teal-d); border-radius: 5px; font-size: 12.5px; font-weight: 700; letter-spacing: .3px; padding: 14px 26px; text-decoration: none; transition: transform .2s, background .2s; box-shadow: 0 10px 28px rgba(211,190,118,.28); border: none; cursor: pointer; }
        .vbtn-primary:hover { background: #e2ce86; transform: translateY(-2px); }
        .vbtn-ghost { display: inline-flex; align-items: center; background: rgba(255,255,255,.1); backdrop-filter: blur(4px); color: #fff; border: 1px solid rgba(255,255,255,.25); border-radius: 5px; font-size: 12.5px; font-weight: 600; letter-spacing: .3px; padding: 14px 24px; text-decoration: none; transition: background .2s, border-color .2s; }
        .vbtn-ghost:hover { border-color: #fff; background: rgba(255,255,255,.18); }

        /* CATEGORY FILTER BADGES */
        .svc-filters { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin: 0 0 40px; }
        .svc-filter-badge {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 10px 18px 10px 14px;
          border-radius: 999px;
          border: 1px solid rgba(41,75,90,.14);
          background: #fff;
          color: var(--teal);
          font-family: 'DM Sans', sans-serif;
          font-size: 12.5px; font-weight: 600; letter-spacing: .2px;
          cursor: pointer;
          transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease, background .2s ease, color .2s ease;
        }
        .svc-filter-badge .ico {
          display: flex; align-items: center; justify-content: center;
          width: 22px; height: 22px; border-radius: 50%;
          background: rgba(41,75,90,.07); color: var(--teal);
          transition: background .2s ease, color .2s ease;
        }
        .svc-filter-badge .count {
          background: rgba(41,75,90,.08); color: var(--teal-l);
          font-size: 10.5px; font-weight: 700; padding: 2px 7px; border-radius: 999px;
          transition: background .2s ease, color .2s ease;
        }
        .svc-filter-badge:hover { border-color: var(--gold); transform: translateY(-2px); box-shadow: var(--shadow-sm); }
        .svc-filter-badge.active {
          background: var(--teal); border-color: var(--teal); color: #fff;
          box-shadow: 0 10px 24px rgba(41,75,90,.28); transform: translateY(-1px);
        }
        .svc-filter-badge.active .ico { background: rgba(211,190,118,.28); color: var(--gold-l); }
        .svc-filter-badge.active .count { background: rgba(211,190,118,.25); color: var(--gold-l); }

        /* SERVICES GRID */
        .svc-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; }
        @media (max-width: 900px) { .svc-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 620px) { .svc-grid { grid-template-columns: 1fr; } }

        .svc-card { 
          position: relative; 
          display: flex; 
          flex-direction: column; 
          justify-content: flex-end; 
          min-height: 280px; 
          border-radius: var(--radius); 
          overflow: hidden; 
          text-decoration: none; 
          box-shadow: var(--shadow-sm); 
          border: 1px solid rgba(41,75,90,.08); 
          transition: transform .3s, box-shadow .3s, border-color .3s; 
          cursor: pointer;
        }
        .svc-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-md); border-color: rgba(211,190,118,.5); }
        @media (min-width: 901px) { .svc-card-lg { grid-column: span 2; min-height: 340px; } }
        
        .svc-card-media { position: absolute; inset: 0; background-size: cover; background-position: center; transition: transform .7s ease; }
        .svc-card:hover .svc-card-media { transform: scale(1.06); }
        
        .svc-card-veil { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(26,49,61,0) 40%, rgba(26,49,61,.88) 100%); }
        .svc-card-content { position: relative; z-index: 1; padding: 22px; }
        .svc-card-tag { display: inline-block; font-size: 10px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; color: var(--gold-l); margin-bottom: 6px; }
        .svc-card-title { font-family: 'Playfair Display', serif; font-weight: 600; font-size: 1.2rem; color: #fff; line-height: 1.25; }
        .svc-card-desc { margin: 6px 0 0; font-size: 12.5px; font-weight: 300; color: rgba(255,255,255,.78); line-height: 1.55; max-width: 320px; }
        
        .svc-chip { 
          position: absolute; 
          right: 14px; 
          top: 14px; 
          z-index: 2; 
          width: 34px; 
          height: 34px; 
          border-radius: 50%; 
          background: var(--gold); 
          color: var(--teal-d); 
          display: flex; 
          align-items: center; 
          justify-content: center; 
          box-shadow: var(--shadow-sm); 
          transition: transform .3s; 
        }
        .svc-card:hover .svc-chip { transform: rotate(45deg); }

        /* FEATURED SERVICE PANEL */
        .svc-featured { display: grid; grid-template-columns: 1.1fr 1fr; border-radius: var(--radius); overflow: hidden; box-shadow: var(--shadow-md); border: 1px solid rgba(41,75,90,.09); background: #fff; }
        @media (max-width: 900px) { .svc-featured { grid-template-columns: 1fr; } }
        .svc-featured-media { position: relative; min-height: 320px; background-size: cover; background-position: center; }
        @media (min-width: 901px) { .svc-featured-media { min-height: 420px; } }
        .svc-featured-content { padding: 44px 42px; display: flex; flex-direction: column; justify-content: center; }
        .svc-featured-title { font-size: clamp(1.6rem, 2.4vw, 2.1rem); font-weight: 600; color: var(--ink); line-height: 1.2; margin: 10px 0 14px; }
        .svc-featured-text { font-size: 14.5px; color: var(--silver); font-weight: 300; line-height: 1.75; margin-bottom: 26px; }

        /* WHY CHOOSE US */
        .why-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 30px; }
        @media (max-width: 900px) { .why-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 560px) { .why-grid { grid-template-columns: 1fr; } }
        .why-item { background: #fff; border: 1px solid rgba(41,75,90,.09); border-radius: var(--radius); padding: 28px 24px; transition: border-color .2s, box-shadow .2s, transform .2s; }
        .why-item:hover { border-color: rgba(211,190,118,.45); box-shadow: var(--shadow-sm); transform: translateY(-3px); }
        .why-ring { width: 44px; height: 44px; border-radius: 50%; border: 1px solid var(--gold); display: flex; align-items: center; justify-content: center; margin-bottom: 16px; background: var(--cream); }
        .why-ring span { font-family: 'Playfair Display', serif; font-weight: 600; color: var(--teal); font-size: 14px; }
        .why-item-title { font-size: 14.5px; font-weight: 600; color: var(--ink); margin-bottom: 8px; }
        .why-item-text { font-size: 12.5px; color: var(--silver); font-weight: 300; line-height: 1.65; }

        /* OMAN EXPERIENCE */
        .oman-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 44px; align-items: center; }
        @media (max-width: 900px) { .oman-layout { grid-template-columns: 1fr; } }
        .oman-images { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .oman-img { height: 230px; border-radius: var(--radius); background-size: cover; background-position: center; box-shadow: var(--shadow-sm); }
        .oman-img.offset { margin-top: 34px; }
        .oman-text { font-size: 14.5px; color: var(--silver); font-weight: 300; line-height: 1.75; margin-top: 14px; }

        /* CTA BANNER */
        .cta-banner { background: var(--teal); border-radius: var(--radius); padding: 52px 48px; display: flex; align-items: center; justify-content: space-between; gap: 30px; flex-wrap: wrap; }
        .cta-banner-title { color: #fff; font-size: 1.5rem; font-weight: 500; margin-bottom: 8px; }
        .cta-banner-sub { color: rgba(255,255,255,.7); font-size: 13.5px; font-weight: 300; max-width: 460px; }

        @media (max-width: 576px) {
          .cta-banner { padding: 32px 24px; }
          .svc-featured-content { padding: 28px 22px; }
        }
      `}</style>

      <div className="visa-page">
        <Header />

        {/* HERO */}
        <section className="visa-hero">
          <div className="visa-hero-img" style={{ backgroundImage: `url(https://images.unsplash.com/photo-1768767601756-17c8b189d5ef?w=1800&q=80&fit=crop&crop=center)` }} />
          <div className="visa-hero-veil" />
          <div className="container visa-hero-inner">
            <div className="eyebrow light" style={{ marginBottom: 14 }}>What We Offer</div>
            <h1 className="visa-hero-title">
              Travel <span className="gold">Your Way</span>
            </h1>
            <p className="visa-hero-sub">
              From corporate itineraries to visas, cruises, and pilgrimage journeys — every
              service here is built around one idea: travel that's arranged with care, so you
              can move through the world without friction.
            </p>
            <div className="visa-hero-ctas">
              <a href="#services" className="vbtn-primary">
                Explore Services
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </a>
              <a href="/contact" className="vbtn-ghost">Speak to an Advisor</a>
            </div>
          </div>
        </section>

        {/* SERVICES INTRO */}
        <section className="section-pad bg-white">
          <div className="container text-center" style={{ maxWidth: 640, margin: '0 auto' }}>
            <span className="eyebrow" style={{ justifyContent: 'center' }}>Full-Service Travel</span>
            <h2 className="section-title">One agency, every part of the journey</h2>
            <p className="section-sub" style={{ margin: '0 auto' }}>
              Flights, visas, hotels, cruises, and ground transport — coordinated by one team so
              nothing falls between the gaps.
            </p>
          </div>
        </section>

        {/* SERVICES SHOWCASE */}
        <section className="section-pad bg-cream" id="services">
          <div className="container">
            {/* Badges de filtre par catégorie */}
            <div className="svc-filters" role="tablist" aria-label="Filter services by category">
              <button
                type="button"
                role="tab"
                aria-selected={activeCategory === null}
                className={`svc-filter-badge ${activeCategory === null ? 'active' : ''}`}
                onClick={() => setActiveCategory(null)}
              >
                <span className="ico">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" />
                  </svg>
                </span>
                All Services
                <span className="count">{services.length}</span>
              </button>

              {CATEGORIES.map((cat) => {
                const count = services.filter((s) => s.category === cat).length;
                return (
                  <button
                    key={cat}
                    type="button"
                    role="tab"
                    aria-selected={activeCategory === cat}
                    className={`svc-filter-badge ${activeCategory === cat ? 'active' : ''}`}
                    onClick={() => setActiveCategory(cat)}
                  >
                    <span className="ico">{CATEGORY_ICONS[cat] || DEFAULT_CATEGORY_ICON}</span>
                    {cat}
                    <span className="count">{count}</span>
                  </button>
                );
              })}
            </div>

            <div className="svc-grid">
              {filteredServices.map((s) => <ServiceCard key={s.id} service={s} />)}
            </div>
          </div>
        </section>



        {/* WHY CHOOSE US */}
        <section className="section-pad bg-cream">
          <div className="container">
            <div style={{ marginBottom: 40 }}>
              <span className="eyebrow">Why Choose Us</span>
              <h2 className="section-title">Careful travel, from the first call</h2>
            </div>
            <div className="why-grid">
              {strengths.map((s, i) => (
                <div className="why-item" key={s.title}>
                  <div className="why-ring"><span>{String(i + 1).padStart(2, '0')}</span></div>
                  <div className="why-item-title">{s.title}</div>
                  <p className="why-item-text">{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>



        <DefaultFooter />
      </div>
    </>
  );
}